import type { ProjectArtifactDetailDto } from '@inference.engine/workspace-api-client/web';
import { ArtifactsService } from '@inference.engine/api-client/web';
import { inferenceHttp } from '@/lib/api/http-client';
import { getFileDownloadUrl } from '@/shared/api/assets.api';
import { AssetPreview } from '@/pages/ProjectDetail/sections/AssetDetail/components/AssetPreview/AssetPreview';
import { PdfViewerProvider } from '@/pages/ProjectDetail/sections/AssetDetail/components/PdfViewer/pdfViewer.store';
import { ResolveArtifactPreview } from './ResolveArtifactPreview';
import { toAssetFile } from './assetFile.utils';
import { useTranslation } from 'react-i18next';

interface Props {
  detail: ProjectArtifactDetailDto;
  artifactId: string;
}

/**
 * Renders a saved (context-less) FILE artifact in the Library preview.
 *
 * Context-less artifacts carry no enrichment on the workspace-api detail path,
 * so its `downloadUrl` is null. The asset id lives in the artifact's data
 * payload on the inference-api, so we read it by id, resolve a fresh signed URL
 * for it, and feed that into the standard `AssetPreview` (which renders inline
 * when it can and falls back to a download card otherwise — e.g. for .xlsx).
 */
export function SavedFileArtifactPreview({ detail, artifactId }: Props) {
  const { t } = useTranslation('projects');

  return (
    <ResolveArtifactPreview
      queryKey="saved-file-artifact-url"
      artifactId={artifactId}
      queryFn={async (workspaceId, id) => {
        const response = await ArtifactsService.getWorkspaceArtifactById({
          client: inferenceHttp,
          path: { workspaceId, artifactId: id },
        });
        const assetId = (response.data?.data as { assetId?: string } | undefined)?.assetId;
        if (!assetId) {
          throw new Error(t('projectDetail.savedFileArtifactPreview.savedFileArtifactHasNo'));
        }
        return getFileDownloadUrl(assetId);
      }}
      render={(downloadUrl) => (
        <PdfViewerProvider>
          <AssetPreview file={{ ...toAssetFile(detail), downloadUrl }} embedded />
        </PdfViewerProvider>
      )}
    />
  );
}
